<template>
  <ValidationObserver ref="observer" tag="section" class="section">
    <b-loading isFullPage :active="isLoading" />
    <div class="columns">
      <h1 class="title column is-size-5 has-text-color-7 mb-0">
        {{
          id ? App.ProfilesScreen.EDIT_PROFILE : App.ProfilesScreen.NEW_PROFILE
        }}
      </h1>
    </div>
    <ValidationProvider v-slot="{ errors }" rules="required">
      <b-field
        :label="General.NAME"
        customClass="has-text-weight-medium has-text-color-7"
        :type="isContrast ? 'is-white' : errors.length ? 'is-refused' : ''"
      >
        <b-input
          v-model="profileName"
          :placeholder="App.ProfilesScreen.PLACEHOLDERS.INSERT_PROFILE_NAME"
        />
        <template #message>
          <ErrorField :errors="errors" />
        </template>
      </b-field>
    </ValidationProvider>
    <b-field
      :label="App.ProfilesScreen.CLONE_PROFILE"
      customClass="has-text-weight-medium has-text-color-7"
    >
      <template slot="label">
        <div class="is-flex is-align-items-center">
          <span>{{ App.ProfilesScreen.CLONE_PROFILE }}</span>
          <span class="optional-label is-capitalized ml-5">
            {{ General.OPTIONAL }}
          </span>
        </div>
      </template>
      <b-autocomplete
        :data="getProfilesListFiltered"
        field="name"
        openOnFocus
        :placeholder="App.ProfilesScreen.PLACEHOLDERS.CLONE"
        @typing="filterProfileList"
        @select="option => fillFieldsByProfile(option)"
      ></b-autocomplete>
    </b-field>
    <b-checkbox
      v-model="all_parts"
      class="my-10 has-text-color-7"
      :type="isContrast ? 'is-white' : 'is-blue-oil'"
    >
      {{ App.ProfilesScreen.PROFILE_APPLICABLE_ALL_PARTS }}
    </b-checkbox>
    <b-field
      horizontal
      :label="General.SELECT_ORGAN_ENTITIES"
      customClass="has-text-weight-medium has-text-left has-text-color-7"
    >
      <CustomTagInput
        v-model="profileParts"
        autocomplete
        :data="getFilteredPartsDistinct(filterPartTagTo)"
        field="name"
        :disabled="all_parts"
        :placeholder="General.PLACEHOLDER.SEARCH_FOR_ORGAN_ENTITY"
        @typing="setFilterPartTagTo"
      />
    </b-field>
    <b-field
      horizontal
      :label="General.KEYWORDS"
      customClass="has-text-weight-medium has-text-left has-text-color-7"
    >
      <CustomTagInput
        v-model="keywords"
        :data="getKeywordsFiltered"
        :loading="isFetching"
        :placeholder="App.ProfilesScreen.PLACEHOLDERS.INSERT_KEYWORDS"
        autocomplete
        allowNew
        @typing="setFilterKeywordTagTo"
      />
    </b-field>
    <b-field
      v-if="hasPermissionAdmMaster"
      :label="General.ASSIGNER_PROFILE"
      customClass="has-text-weight-medium has-text-left has-text-color-7"
    >
      <div>
        <b-checkbox
          v-for="profile in assignerOptions"
          :key="profile.id"
          v-model="profiles_can_assign"
          :nativeValue="profile.id"
          :type="isContrast ? 'is-white' : 'is-blue-oil'"
          class="profile-checkbox has-text-color-7"
          >{{ profile.name }}
        </b-checkbox>
      </div>
    </b-field>
    <b-field customClass="has-text-weight-semibold has-text-color-7 mt-30 mb-0">
      <template #label>
        <div>{{ General.PERMISSIONS }}</div>
      </template>

      <div class="is-flex is-flex-direction-column">
        <div class="mb-10" :class="{ 'is-align-self-flex-end': !isMobile }">
          (<button class="select-button" @click="checkAll(true)">
            {{ App.ProfilesScreen.SELECT_ALL }}
          </button>
          |
          <button class="select-button" @click="checkAll(false)">
            {{ App.ProfilesScreen.UNCHECK_ALL }}</button
          >)
        </div>

        <div class="permission-container">
          <div
            v-for="permission in profilePermissions"
            :key="permission.id"
            class="permission-checkbox"
          >
            <b-checkbox
              v-model="permission.active"
              :type="isContrast ? 'is-white' : 'is-blue-oil'"
              class="custom-break has-text-color-7 is-align-items-flex-start"
            >
              {{ permission.name }}
            </b-checkbox>
            <b-tooltip
              v-if="permission.description"
              :label="permission.description"
              multilined
              :type="isContrast ? 'is-white' : 'is-blue-oil'"
              :position="isMobile ? 'is-left' : 'is-right'"
              class="ml-5"
            >
              <b-icon
                :type="isContrast ? 'is-white' : 'is-blue-oil'"
                icon="information"
                size="is-small"
              />
            </b-tooltip>
          </div>
        </div>
      </div>
    </b-field>
    <div class="buttons" :class="{ 'is-flex-direction-column': isMobile }">
      <b-button
        :label="General.SAVE"
        :type="isContrast ? 'is-white' : 'is-green-lawn'"
        class="has-text-weight-semibold"
        :disabled="isLoading"
        :expanded="isMobile"
        @click="createProfile"
      />
      <b-button
        :label="General.CANCEL"
        :type="isContrast ? 'is-white' : 'is-orange'"
        class="has-text-weight-semibold"
        outlined
        :expanded="isMobile"
        @click="cancel()"
      />
    </div>
  </ValidationObserver>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { App, General } from "@/utils/constants";
import {
  USER_PERMISSIONS as userPermissions,
  ASSIGNER_PROFILES_OPTIONS as assignerOptions
} from "@/utils/enumerate";

import debounce from "lodash/debounce";

export default {
  name: "ProfileEditor",
  props: {
    id: {
      type: String,
      default: ""
    }
  },
  data() {
    return {
      App,
      General,
      assignerOptions,
      isLoading: false,
      isFetching: false,
      filterPartTagTo: "",
      filterKeywordTagTo: "",
      textToFilterProfileList: "",
      profileName: "",
      profileParts: [],
      all_parts: null,
      keywords: [],
      profilePermissions: [],
      profiles_can_assign: []
    };
  },
  computed: {
    ...mapGetters({
      getFilteredPartsDistinct: "getFilteredPartsDistinct",
      getProfileKeywords: "getProfileKeywords",
      getPermission: "getPermission",
      hasPermission: "hasPermission",
      getProfile: "getProfile",
      getProfiles: "getProfiles",
      isContrast: "isContrast",
      isMobile: "isMobile"
    }),

    getProfilesListFiltered() {
      return this.textToFilterProfileList
        ? this.getProfiles.results.filter(profile =>
            profile.name
              .toLowerCase()
              .includes(this.textToFilterProfileList.toLowerCase())
          )
        : this.getProfiles.results;
    },

    getKeywordsFiltered() {
      return this.getProfileKeywords.map(keyword => keyword.keyword);
    },

    hasPermissionAdmMaster() {
      return this.hasPermission(userPermissions["MANAGE_USER_ADM"]);
    }
  },
  async beforeMount() {
    this.isLoading = true;
    await this.profileRequest({ page: 1, paginator_count: 999, search: "" });
    await this.partDistinctRequest();
    await this.getAllPermissionsRequest({
      page: 1,
      paginator_count: 900,
      order_by: "id"
    });

    this.initializeData();
    this.isLoading = false;
  },
  methods: {
    ...mapActions({
      createProfileRequest: "createProfileRequest",
      profileKeywordsRequest: "profileKeywordsRequest",
      partDistinctRequest: "partDistinctRequest",
      getAllPermissionsRequest: "getAllPermissionsRequest",
      profileByIdRequest: "profileByIdRequest",
      updateProfileRequest: "updateProfileRequest",
      profileRequest: "profileRequest"
    }),

    filterProfileList: debounce(async function(text) {
      this.textToFilterProfileList = text;
    }, 500),

    requestKeywords: debounce(async function(keyword) {
      this.isFetching = true;
      await this.profileKeywordsRequest(keyword);
      this.isFetching = false;
    }, 500),

    setFilterPartTagTo(text) {
      this.filterPartTagTo = text;
    },

    setFilterKeywordTagTo(text) {
      this.filterKeywordTagTo = text;
      this.requestKeywords(text);
    },

    checkAll(check = true) {
      this.profilePermissions.forEach(permission => {
        permission.active = check;
      });
    },

    fillFieldsByProfile(profile) {
      this.profileName = profile.name;
      this.all_parts = profile.all_parts;
      this.profileParts = [...profile.parts];
      this.keywords = profile.keywords.map(keyword => keyword.keyword);
      this.profiles_can_assign = profile.profiles_can_assign;

      profile.permissions.forEach(permissionReceived => {
        this.profilePermissions.find(
          permissionToChange => permissionReceived.id === permissionToChange.id
        ).active = true;
      });
    },

    async validate() {
      if (!(await this.$refs.observer.validate())) {
        this.$refs.observer.$el.scrollIntoView({
          behavior: "smooth"
        });
      }
      const msg = [];

      if (!this.profileName) msg.push(App.ProfilesScreen.MESSAGES.INSERT_NAME);

      if (!this.profilePermissions.some(permission => permission.active))
        msg.push(App.ProfilesScreen.MESSAGES.INSERT_PERMISSIONS);

      if (!this.all_parts && !this.profileParts.length)
        msg.push(App.ProfilesScreen.MESSAGES.INSERT_PART);

      if (msg.length)
        this.snackToast.open({
          message: `${msg.join(", ")}.`,
          type: "is-danger",
          duration: 8000
        });

      return !msg.length;
    },

    async createProfile() {
      if (await this.validate()) {
        const profileData = {
          name: this.profileName,
          parts: this.profileParts,
          all_parts: this.all_parts,
          keywords: this.keywords,
          profiles_can_assign: this.profiles_can_assign,
          permissions: this.profilePermissions
            .filter(permission => permission.active)
            .map(permissionFiltered => permissionFiltered.id)
        };

        this.isLoading = true;
        const status = this.id
          ? await this.updateProfileRequest({
              ...profileData,
              profileId: this.id
            })
          : await this.createProfileRequest(profileData);

        this.isLoading = false;

        if (status) this.$router.push("/app/admin/perfis");
      }
    },

    cancel() {
      this.$router.push("/app/admin/perfis");
    },

    async initializeData() {
      this.profilePermissions = this.getPermission.list;
      this.profilePermissions = this.profilePermissions.map(permission => ({
        ...permission,
        active: false
      }));

      if (this.id) {
        await this.profileByIdRequest(this.id);

        this.fillFieldsByProfile(this.getProfile);
      }
    }
  }
};
</script>

<style scoped lang="scss">
@import "~bulma/sass/utilities/_all";
@import "@/assets/scss/variables";

.section {
  background-color: $color-1;
  padding-top: 20px;

  .permission-container {
    -webkit-column-count: 3;
    -moz-column-count: 3;
    column-count: 3;

    @media screen and (max-width: $tablet) {
      column-count: 1;
    }

    @media screen and (min-width: $tablet) and (max-width: $desktop) {
      column-count: 2;
    }
  }

  .permission-checkbox {
    display: flex;
    width: 100%;
    margin: 10px 0;
  }

  .profile-checkbox {
    margin: 10px;
  }

  .select-button {
    background: none;
    color: inherit;
    border: none;
    padding: 0;
    font: inherit;
    cursor: pointer;
  }

  button.select-button:hover {
    color: $green-dark;
  }

  .optional-label {
    font-size: 12px;
    font-weight: 300;
    color: $color-13;
  }
}
</style>
